import React, { useState } from "react";
import { getClientGoszakup, recalcClient } from "../../api/paidClientsService";
import useClientData from "../../hooks/useClientData";
import { APPLICATION_RESULTS, GOSZAKUP_WARNINGS } from "../../utils/constants";
import { fmtBin, fmtDate, fmtDateTime, fmtMoney, fmtNum, fmtPct } from "../../utils/format";
import { TabState } from "../common";

const PERIODS = [
  { key: "subscription", label: "За подписку", pct: "winPctSubscription" },
  { key: "before12m", label: "12 мес до подписки", pct: "winPctBefore12m" },
  { key: "allTime", label: "За всё время", pct: "winPctAllTime" },
];

// null в окне → «—»
const ROWS = [
  { label: "Заявки", get: (w) => fmtNum(w.applications) },
  { label: "Победы", get: (w) => fmtNum(w.wins) },
  { label: "% побед", get: (w, d, p) => fmtPct(d[p.pct]) },
  { label: "Договоры", get: (w) => fmtNum(w.contracts) },
  { label: "Сумма договоров", get: (w) => fmtMoney(w.contractsSum) },
  { label: "Заявки на лоты из лайков", get: (w) => fmtNum(w.appsFromLikes) },
  { label: "Победы на лотах из лайков", get: (w) => fmtNum(w.winsFromLikes) },
];

const GoszakupTab = ({ userId, detail, notify, goTab, onChanged }) => {
  const { data, setData, loading, error, reload } = useClientData(getClientGoszakup, userId);
  const [recalcing, setRecalcing] = useState(false);

  const hasBin = (detail.passport?.bins || []).length > 0;

  const recalc = async () => {
    setRecalcing(true);
    try {
      const fresh = await recalcClient(userId);
      notify("Пересчёт выполнен");
      if (fresh) setData((prev) => ({ ...(prev || {}), ...fresh }));
      reload();
      onChanged();
    } catch (e) {
      notify(e.code === "BigDataError" ? "Сервис госзакупок недоступен, попробуйте позже" : e.message);
    } finally {
      setRecalcing(false);
    }
  };

  const windows = data?.windows;
  const apps = data?.lastApplications || [];

  return (
    <>
      {!hasBin && (
        <div className="alert">
          У клиента не указан БИН — статистика участий и побед в Госзакупках недоступна. Пока БИН
          не добавлен, смотрите вкладку «Ниша» (ориентир по лайкам) и «Активность».
          <div style={{ marginTop: 8 }}>
            <button type="button" className="pc-btn sm" onClick={() => goTab("passport")}>
              Перейти к добавлению БИН
            </button>
          </div>
        </div>
      )}
      {(data?.warnings || []).map((w) => (
        <div key={w} className={`alert ${w === "distributor" ? "info" : ""}`}>
          {GOSZAKUP_WARNINGS[w] || w}
        </div>
      ))}

      <TabState loading={loading} error={error} data={data}>
        {!windows ? (
          <div className="pc-card muted">Статистику госзакупок по клиенту ещё не считали.</div>
        ) : (
          <div className="pc-card">
            <h3>Результаты по всем БИН клиента</h3>
            <table className="pc-table">
              <thead>
                <tr>
                  <th />
                  {PERIODS.map((p) => (
                    <th key={p.key} className="r">
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map((row) => (
                  <tr key={row.label}>
                    <td className="muted">{row.label}</td>
                    {PERIODS.map((p) => (
                      <td key={p.key} className="r">
                        {row.get(windows[p.key] || {}, data, p)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="pc-card">
          <h3>Последние заявки</h3>
          {apps.length ? (
            <div style={{ overflowX: "auto" }}>
              <table className="pc-table small">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Лот</th>
                    <th>Заказчик</th>
                    <th className="r">Сумма</th>
                    <th>Итог</th>
                    <th>Был в лайках</th>
                    <th>БИН</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.slice(0, 20).map((a, i) => {
                    const res = APPLICATION_RESULTS[a.result];
                    return (
                      <tr key={`${a.lotId}-${a.bin}-${i}`}>
                        <td>{fmtDate(a.date)}</td>
                        <td>
                          {/* формат ссылки на goszakup.gov.kz ещё не согласован — пока без ссылки */}
                          <div>{a.lotName || "—"}</div>
                          {a.lotNumber && <div className="muted mono">{a.lotNumber}</div>}
                        </td>
                        <td>{a.customerName || "—"}</td>
                        <td className="r">{fmtMoney(a.amount)}</td>
                        <td>
                          {res ? <span className={`tag ${res.cls}`}>{res.label}</span> : a.result || "—"}
                        </td>
                        <td>{a.wasLiked ? <span className="tag ok">да</span> : <span className="tag">нет</span>}</td>
                        <td className="mono">{a.bin ? fmtBin(a.bin) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="muted">Заявок нет</div>
          )}
        </div>
      </TabState>

      <div className="pc-row muted">
        {data?.calculatedAt
          ? `Данные госзакупок рассчитаны: ${fmtDateTime(data.calculatedAt)}`
          : "Данные госзакупок ещё не рассчитаны"}
        <button type="button" className="pc-btn sm" onClick={recalc} disabled={recalcing}>
          {recalcing ? "Пересчитываю… это может занять несколько секунд" : "🔄 Пересчитать сейчас"}
        </button>
      </div>
    </>
  );
};

export default GoszakupTab;
